import moment from 'moment';
import { defineStore } from 'pinia';

import { trans } from '@bv/utils';

/**
 * @interface Timer
 * @description Represents a single timer object.
 * Now includes its own update frequency and last update timestamp.
 */
export interface Timer {
  id: number;
  startTime: number;
  duration: string;
  timeDiff?: number; // Diff time between local and actual time
  frequency: number; // Update interval in seconds
  lastUpdated: number; // Timestamp of the last update
}

/**
 * @type TimerFrequency
 * @description Defines the allowed frequencies for a timer update by seconds.
 */
export type TimerFrequency = number;

/**
 * @interface TimerState
 * @description Defines the shape of the state within the store.
 */
interface TimerState {
  _timers: Map<number, Timer>;
  _updateCycleActive: boolean;
  _updateTimeout: ReturnType<typeof setTimeout> | null;
  _nextTimerId: number;
}

/**
 * Timer Store
 */
export const timerStore = defineStore('bvTimer', {
  state: (): TimerState => ({
    _timers: new Map<number, Timer>(),
    _updateCycleActive: false,
    _updateTimeout: null,
    _nextTimerId: 0,
  }),
  actions: {
    /**
     * Registers a new timer with the store.
     * @param {number | string | undefined} startTime - The initial timestamp (in milliseconds).
     *                                            If undefined, the current time will be used.
     * @param {TimerFrequency} frequencyInSeconds - The update interval in seconds. Defaults to 1.
     * @param {number | undefined} [timeDiff=undefined] - The diff between local & actual time.
     * @returns {Timer} The newly created reactive timer instance.
     */
    registerTimer(
      startTime?: number | string,
      frequencyInSeconds: TimerFrequency = 1,
      timeDiff: number | undefined = undefined,
    ): Timer {
      const newId = this._generateTimerId();

      const newTimer: Timer = {
        id: newId,
        startTime: startTime
          ? typeof startTime === 'number'
            ? startTime
            : moment(startTime).valueOf()
          : moment().valueOf(),
        duration: '',
        timeDiff: timeDiff,
        frequency: frequencyInSeconds,
        lastUpdated: 0, // Set to 0 to force an immediate first update
      };

      this._timers.set(newId, newTimer);
      this._startUpdateCycle();

      return newTimer;
    },

    /**
     * Un-registers/removes a timer from the store.
     * @param {number} id - The ID of the timer to remove.
     */
    unregisterTimer(id: number): void {
      this._timers.delete(id);
      // If no timers are left, stop the update cycle
      if (this._timers.size === 0) {
        this._stopUpdateCycle();
      }
    },

    /**
     * Generates a new auto-incremented numeric ID for timers.
     * @private
     */
    _generateTimerId(): number {
      return this._nextTimerId++;
    },

    /**
     * Starts the main update cycle if it's not already running.
     * @private
     */
    _startUpdateCycle(): void {
      if (this._updateCycleActive) {
        return;
      }
      this._updateCycleActive = true;
      // The loop runs every second, but updates timers based on their individual frequency.
      this._updateTimeout = setTimeout(() => this._refreshTimers(), 1000);
    },

    /**
     * Stops the main update cycle.
     * @private
     */
    _stopUpdateCycle(): void {
      if (this._updateTimeout) {
        clearTimeout(this._updateTimeout);
      }
      this._updateCycleActive = false;
    },

    /**
     * The core loop that checks and updates all timers.
     * @private
     */
    _refreshTimers(): void {
      const now = Date.now();

      this._timers.forEach((timer) => {
        const elapsed = now - timer.lastUpdated;
        // Check if enough time has passed based on the timer's specific frequency
        if (elapsed >= timer.frequency * 1000) {
          this._calculateTimerValue(timer);
          timer.lastUpdated = now; // Record the time of this update
        }
      });

      // Schedule the next check
      this._updateCycleActive = false;
      this._startUpdateCycle();
    },

    /**
     * Calculates and formats the current duration for a single timer.
     * @param {Timer} timer - The timer instance to update.
     * @private
     */
    _calculateTimerValue(timer: Timer): void {
      const now: number = moment.utc(moment.now()).valueOf();
      const toTime: number = timer.startTime - (timer.timeDiff || 0);

      const isCountdown = toTime > now;
      const diff = isCountdown ? toTime - now : now - toTime;

      const durationValue = moment.utc(diff);
      const durationData = moment.duration(durationValue.valueOf());

      const months = durationData.months()
        ? trans('global.n_months_trans', { months: durationData.months() })
        : undefined;
      const days = durationData.days() ? trans('global.n_days_trans', { days: durationData.days() }) : undefined;
      const time = durationValue.format('HH:mm:ss');

      timer.duration = [months, days, time].filter(Boolean).join(', ');
    },
  },
});
